import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  EuiPanel,
  EuiTitle,
  EuiSpacer,
  EuiText,
  EuiBadge,
  EuiButton,
  EuiButtonEmpty,
  EuiButtonGroup,
  EuiComboBox,
  EuiFlexGroup,
  EuiFlexItem,
  EuiLoadingSpinner,
  EuiBasicTableColumn,
  EuiInMemoryTable,
  EuiMarkdownFormat,
  EuiLink,
  EuiEmptyPrompt,
  EuiCallOut,
  EuiHorizontalRule,
  EuiAvatar,
  EuiToolTip,
  EuiIcon,
  EuiCode,
  EuiTextColor,
  EuiFieldSearch,
  EuiPopover,
  EuiContextMenuPanel,
  EuiContextMenuItem,
  EuiHealth,
} from "@elastic/eui";
import { OpsanioApplianceData } from "..";
import { useApi } from "src/api";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { useToast } from "src/notifications";
import { useAnketaI18n } from "src/internationalization";
import { GitLabIssue, GitLabNote } from "src/questionnaires";

type View = "list" | "detail";
type StateFilter = "all" | "opened" | "closed";
type TranslateFn = ReturnType<typeof useAnketaI18n>["translate"];

interface DateRangeOption {
  key: string;
  labelKey: string;
  labelDefault: string;
  days: number | null;
}

const DATE_RANGE_OPTIONS: DateRangeOption[] = [
  {
    key: "all",
    labelKey: "anketa.opsanio.appliance.flyout.gitlab.dateRange.all",
    labelDefault: "All",
    days: null,
  },
  {
    key: "1d",
    labelKey: "anketa.opsanio.appliance.flyout.gitlab.dateRange.1d",
    labelDefault: "Last 24h",
    days: 1,
  },
  {
    key: "7d",
    labelKey: "anketa.opsanio.appliance.flyout.gitlab.dateRange.7d",
    labelDefault: "Last 7 days",
    days: 7,
  },
  {
    key: "30d",
    labelKey: "anketa.opsanio.appliance.flyout.gitlab.dateRange.30d",
    labelDefault: "Last month",
    days: 30,
  },
  {
    key: "90d",
    labelKey: "anketa.opsanio.appliance.flyout.gitlab.dateRange.90d",
    labelDefault: "Last quarter",
    days: 90,
  },
];

function relativeTime(dateString: string, translate: TranslateFn): string {
  const diff = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  if (diff < 60)
    return translate("anketa.opsanio.appliance.flyout.time.justNow", {
      defaultMessage: "Just now",
    });
  if (diff < 3600)
    return translate("anketa.opsanio.appliance.flyout.time.minutesAgo", {
      values: { count: Math.floor(diff / 60) },
      defaultMessage: "{{count}} min ago",
    });
  if (diff < 86400)
    return translate("anketa.opsanio.appliance.flyout.time.hoursAgo", {
      values: { count: Math.floor(diff / 3600) },
      defaultMessage: "{{count}} h ago",
    });
  if (diff < 2592000)
    return translate("anketa.opsanio.appliance.flyout.time.daysAgo", {
      values: { count: Math.floor(diff / 86400) },
      defaultMessage: "{{count}} days ago",
    });
  return new Date(dateString).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const StateBadge = ({
  state,
  translate,
}: {
  state: string;
  translate: TranslateFn;
}) => (
  <EuiHealth
    color={state === "opened" ? "success" : "subdued"}
    style={{ fontSize: 12 }}
  >
    {state === "opened"
      ? translate("anketa.opsanio.appliance.flyout.gitlab.state.opened", {
          defaultMessage: "Open",
        })
      : translate("anketa.opsanio.appliance.flyout.gitlab.state.closed", {
          defaultMessage: "Closed",
        })}
  </EuiHealth>
);

const AssigneeCell = ({ assignee }: { assignee: { name: string } | null }) =>
  assignee ? (
    <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
      <EuiFlexItem grow={false}>
        <EuiAvatar name={assignee.name} size="s" />
      </EuiFlexItem>
      <EuiFlexItem>
        <EuiText size="xs">{assignee.name}</EuiText>
      </EuiFlexItem>
    </EuiFlexGroup>
  ) : (
    <EuiText size="xs" color="subdued">
      -
    </EuiText>
  );

const NoteCard = ({
  note,
  issueUrl,
  formatDate,
  translate,
}: {
  note: GitLabNote;
  issueUrl: string;
  formatDate: (d: string) => string;
  translate: TranslateFn;
}) => (
  <EuiPanel
    paddingSize="m"
    hasShadow={false}
    hasBorder
    style={{ marginBottom: 8 }}
  >
    <EuiFlexGroup alignItems="flexStart" gutterSize="m" responsive={false}>
      <EuiFlexItem grow={false}>
        <EuiAvatar name={note.author?.name ?? "?"} size="m" />
      </EuiFlexItem>
      <EuiFlexItem>
        <EuiFlexGroup
          justifyContent="spaceBetween"
          alignItems="center"
          gutterSize="s"
          responsive={false}
        >
          <EuiFlexItem grow={false}>
            <EuiText size="s">
              <strong>
                <EuiLink target="_blank" href={issueUrl}>
                  {note.author?.name ??
                    translate("anketa.opsanio.appliance.flyout.gitlab.unknownUser", {
                      defaultMessage: "Unknown",
                    })}
                </EuiLink>
              </strong>
            </EuiText>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiToolTip content={formatDate(note.created_at)}>
              <EuiTextColor color="subdued">
                <EuiText size="xs">
                  {relativeTime(note.created_at, translate)}
                </EuiText>
              </EuiTextColor>
            </EuiToolTip>
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiSpacer size="xs" />
        <EuiText size="s" style={{ whiteSpace: "pre-wrap" }}>
          {note.body}
        </EuiText>
      </EuiFlexItem>
    </EuiFlexGroup>
  </EuiPanel>
);

interface FilterBarProps {
  issues: GitLabIssue[];
  searchText: string;
  onSearchChange: (v: string) => void;
  stateFilter: StateFilter;
  onStateChange: (v: StateFilter) => void;
  selectedAssignees: string[];
  onAssigneesChange: (v: string[]) => void;
  dateRange: string;
  onDateRangeChange: (v: string) => void;
  filteredCount: number;
  totalCount: number;
  onReset: () => void;
  translate: TranslateFn;
}

const FilterBar = ({
  issues,
  searchText,
  onSearchChange,
  stateFilter,
  onStateChange,
  selectedAssignees,
  onAssigneesChange,
  dateRange,
  onDateRangeChange,
  filteredCount,
  totalCount,
  onReset,
  translate,
}: FilterBarProps) => {
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);

  const userOptions = useMemo(() => {
    const names = new Set<string>();
    issues.forEach((i) => {
      if (i.assignee?.name) names.add(i.assignee.name);
      if (i.author?.name) names.add(i.author.name);
    });
    return Array.from(names)
      .sort()
      .map((n) => ({ label: n }));
  }, [issues]);

  const selectedAssigneeOptions = useMemo(
    () => selectedAssignees.map((n) => ({ label: n })),
    [selectedAssignees],
  );

  const stateButtons = [
    {
      id: "all",
      label: translate("anketa.opsanio.appliance.flyout.gitlab.state.all", {
        defaultMessage: "All",
      }),
    },
    {
      id: "opened",
      label: translate("anketa.opsanio.appliance.flyout.gitlab.state.opened", {
        defaultMessage: "Open",
      }),
    },
    {
      id: "closed",
      label: translate("anketa.opsanio.appliance.flyout.gitlab.state.closed", {
        defaultMessage: "Closed",
      }),
    },
  ];

  const currentDateOption = DATE_RANGE_OPTIONS.find((o) => o.key === dateRange);
  const currentDateLabel = currentDateOption
    ? translate(currentDateOption.labelKey, {
        defaultMessage: currentDateOption.labelDefault,
      })
    : translate("anketa.opsanio.appliance.flyout.gitlab.dateRange.all", {
        defaultMessage: "All",
      });

  const hasActiveFilters =
    stateFilter !== "all" ||
    selectedAssignees.length > 0 ||
    dateRange !== "all" ||
    searchText !== "";

  return (
    <EuiPanel
      paddingSize="m"
      hasShadow={false}
      color="subdued"
      hasBorder
      style={{ marginBottom: 12 }}
    >
      <EuiFlexGroup gutterSize="m" alignItems="center" responsive={false} wrap>
        <EuiFlexItem>
          <EuiFieldSearch
            placeholder={translate(
              "anketa.opsanio.appliance.flyout.gitlab.search.placeholder",
              { defaultMessage: "Search issues… (title, labels, users)" },
            )}
            value={searchText}
            onChange={(e) => onSearchChange(e.target.value)}
            isClearable
            compressed
            fullWidth
          />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButtonGroup
            legend={translate(
              "anketa.opsanio.appliance.flyout.gitlab.state.legend",
              { defaultMessage: "Status" },
            )}
            options={stateButtons}
            idSelected={stateFilter}
            onChange={(id) => onStateChange(id as StateFilter)}
            buttonSize="compressed"
            color="primary"
          />
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer size="s" />

      <EuiFlexGroup gutterSize="m" alignItems="center" responsive={false} wrap>
        <EuiFlexItem style={{ minWidth: 200, maxWidth: 320 }}>
          <EuiComboBox
            placeholder={translate(
              "anketa.opsanio.appliance.flyout.gitlab.userFilter.placeholder",
              { defaultMessage: "Filter by user…" },
            )}
            options={userOptions}
            selectedOptions={selectedAssigneeOptions}
            onChange={(opts) => onAssigneesChange(opts.map((o) => o.label))}
            isClearable
            compressed
            aria-label={translate(
              "anketa.opsanio.appliance.flyout.gitlab.userFilter.aria",
              { defaultMessage: "Filter by user" },
            )}
            prepend={<EuiIcon type="user" />}
          />
        </EuiFlexItem>

        <EuiFlexItem grow={false}>
          <EuiPopover
            isOpen={datePopoverOpen}
            closePopover={() => setDatePopoverOpen(false)}
            button={
              <EuiButton
                size="s"
                iconType="calendar"
                iconSide="left"
                color={dateRange !== "all" ? "primary" : "text"}
                onClick={() => setDatePopoverOpen((v) => !v)}
              >
                {currentDateLabel}
              </EuiButton>
            }
          >
            <EuiContextMenuPanel
              items={DATE_RANGE_OPTIONS.map((opt) => (
                <EuiContextMenuItem
                  key={opt.key}
                  icon={dateRange === opt.key ? "check" : "empty"}
                  onClick={() => {
                    onDateRangeChange(opt.key);
                    setDatePopoverOpen(false);
                  }}
                >
                  {translate(opt.labelKey, {
                    defaultMessage: opt.labelDefault,
                  })}
                </EuiContextMenuItem>
              ))}
            />
          </EuiPopover>
        </EuiFlexItem>

        <EuiFlexItem grow={false}>
          <EuiText size="xs" color="subdued">
            {filteredCount === totalCount
              ? translate(
                  "anketa.opsanio.appliance.flyout.gitlab.count.total",
                  {
                    values: { total: totalCount },
                    defaultMessage: "{{total}} issues",
                  },
                )
              : translate(
                  "anketa.opsanio.appliance.flyout.gitlab.count.filtered",
                  {
                    values: { filtered: filteredCount, total: totalCount },
                    defaultMessage: "{{filtered}} of {{total}} issues",
                  },
                )}
          </EuiText>
        </EuiFlexItem>

        {hasActiveFilters && (
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty
              size="xs"
              color="danger"
              onClick={onReset}
              iconType="cross"
            >
              {translate("anketa.common.reset.filters")}
            </EuiButtonEmpty>
          </EuiFlexItem>
        )}
      </EuiFlexGroup>
    </EuiPanel>
  );
};

const IssueDetailView = ({
  issue,
  notes,
  loadingNotes,
  formatDate,
  onBack,
  translate,
}: {
  issue: GitLabIssue;
  notes: GitLabNote[];
  loadingNotes: boolean;
  formatDate: (d: string) => string;
  onBack: () => void;
  translate: TranslateFn;
}) => (
  <div style={{ display: "flex", flexDirection: "column" }}>
    <EuiButtonEmpty
      iconType="arrowLeft"
      size="s"
      onClick={onBack}
      style={{ alignSelf: "flex-start", marginBottom: 8 }}
    >
      {translate("anketa.opsanio.appliance.flyout.gitlab.detail.back", {
        defaultMessage: "Back to overview",
      })}
    </EuiButtonEmpty>

    <EuiPanel paddingSize="m" hasShadow={false} color="subdued" hasBorder>
      <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
        <EuiFlexItem grow={false}>
          <StateBadge state={issue.state} translate={translate} />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiCode style={{ fontSize: 12 }}>#{issue.iid}</EuiCode>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size="xs" />
      <EuiTitle size="s">
        <h3>
          <EuiLink target="_blank" href={issue.web_url} external>
            {issue.title}
          </EuiLink>
        </h3>
      </EuiTitle>
      <EuiSpacer size="xs" />
      <EuiFlexGroup gutterSize="m" alignItems="center" responsive={false} wrap>
        <EuiFlexItem grow={false}>
          <EuiText size="xs" color="subdued">
            <EuiIcon type="user" size="s" /> {issue.author?.name ?? "-"}
          </EuiText>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiToolTip content={formatDate(issue.created_at)}>
            <EuiText size="xs" color="subdued">
              <EuiIcon type="clock" size="s" />{" "}
              {translate(
                "anketa.opsanio.appliance.flyout.gitlab.detail.created",
                {
                  values: { time: relativeTime(issue.created_at, translate) },
                  defaultMessage: "Created {{time}}",
                },
              )}
            </EuiText>
          </EuiToolTip>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiToolTip content={formatDate(issue.updated_at)}>
            <EuiText size="xs" color="subdued">
              <EuiIcon type="refresh" size="s" />{" "}
              {translate(
                "anketa.opsanio.appliance.flyout.gitlab.detail.updated",
                {
                  values: { time: relativeTime(issue.updated_at, translate) },
                  defaultMessage: "Updated {{time}}",
                },
              )}
            </EuiText>
          </EuiToolTip>
        </EuiFlexItem>
        {issue.assignee && (
          <EuiFlexItem grow={false}>
            <EuiFlexGroup
              gutterSize="xs"
              alignItems="center"
              responsive={false}
            >
              <EuiFlexItem grow={false}>
                <EuiAvatar name={issue.assignee.name} size="s" />
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiText size="xs" color="subdued">
                  {issue.assignee.name}
                </EuiText>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>
        )}
      </EuiFlexGroup>
      {issue.labels && issue.labels.length > 0 && (
        <>
          <EuiSpacer size="s" />
          <EuiFlexGroup gutterSize="xs" wrap responsive={false}>
            {issue.labels.map((label) => (
              <EuiFlexItem grow={false} key={label}>
                <EuiBadge color="hollow">{label}</EuiBadge>
              </EuiFlexItem>
            ))}
          </EuiFlexGroup>
        </>
      )}
    </EuiPanel>

    <EuiSpacer size="m" />

    <EuiPanel paddingSize="m" hasShadow={false} hasBorder>
      <EuiText
        size="xs"
        color="subdued"
        style={{
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontWeight: 600,
          marginBottom: 8,
        }}
      >
        {translate(
          "anketa.opsanio.appliance.flyout.gitlab.detail.description",
          { defaultMessage: "Description" },
        )}
      </EuiText>
      {issue.description ? (
        <EuiMarkdownFormat>{issue.description}</EuiMarkdownFormat>
      ) : (
        <EuiText size="s" color="subdued">
          <em>
            {translate(
              "anketa.opsanio.appliance.flyout.gitlab.detail.description.empty",
              { defaultMessage: "No description provided." },
            )}
          </em>
        </EuiText>
      )}
    </EuiPanel>

    <EuiSpacer size="m" />

    <EuiFlexGroup
      gutterSize="s"
      alignItems="center"
      responsive={false}
      style={{ marginBottom: 8 }}
    >
      <EuiFlexItem grow={false}>
        <EuiText
          size="xs"
          color="subdued"
          style={{
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            fontWeight: 600,
          }}
        >
          {translate(
            "anketa.opsanio.appliance.flyout.gitlab.detail.comments",
            { defaultMessage: "Comments" },
          )}
        </EuiText>
      </EuiFlexItem>
      {!loadingNotes && (
        <EuiFlexItem grow={false}>
          <EuiBadge color="hollow">{notes.length}</EuiBadge>
        </EuiFlexItem>
      )}
    </EuiFlexGroup>

    {loadingNotes ? (
      <EuiFlexGroup justifyContent="center" style={{ padding: "24px 0" }}>
        <EuiLoadingSpinner size="l" />
      </EuiFlexGroup>
    ) : notes.length === 0 ? (
      <EuiText size="s" color="subdued">
        <em>
          {translate(
            "anketa.opsanio.appliance.flyout.gitlab.detail.comments.empty",
            { defaultMessage: "No comments yet." },
          )}
        </em>
      </EuiText>
    ) : (
      notes.map((note) => (
        <NoteCard
          key={note.id}
          note={note}
          issueUrl={issue.web_url}
          formatDate={formatDate}
          translate={translate}
        />
      ))
    )}
  </div>
);

export const OpsanioApplianceFlyoutGitLabIssues = ({
  appliance,
}: {
  appliance: OpsanioApplianceData;
}): JSX.Element => {
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();
  const { addToast } = useToast();
  const { translate } = useAnketaI18n();

  const [issues, setIssues] = useState<GitLabIssue[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIssue, setSelected] = useState<GitLabIssue | null>(null);
  const [notes, setNotes] = useState<GitLabNote[]>([]);
  const [loadingNotes, setLoadingNotes] = useState<boolean>(false);
  const [view, setView] = useState<View>("list");

  const [searchText, setSearchText] = useState("");
  const [stateFilter, setStateFilter] = useState<StateFilter>("all");
  const [selectedAssignees, setAssignees] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<string>("all");

  const i18nPrefix = "anketa.opsanio.appliance.gitlab.issue";

  const canFetch = useMemo<boolean>(
    () => typeof keycloak?.token === "string",
    [keycloak],
  );

  const formatDate = useCallback(
    (dateString: string) =>
      new Date(dateString).toLocaleString("de-DE", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    [],
  );

  const getIssues = useCallback(async () => {
    if (!canFetch) return;
    setLoading(true);
    setError(null);
    setSelected(null);
    setView("list");

    try {
      const { project, owner, stage } = appliance.common.metadata;
      const { data, error } = await Get({
        path: `v2/${AnketaResourcesEnum.opsanio}/gitlab/issues/${project}/${owner}/${stage}`,
        responseType: "json",
        token: keycloak.token,
      });

      if (error) {
        setError(error.message);
        addToast({
          title: translate(`${i18nPrefix}.fetch.error`),
          color: "danger",
          text: error.message,
        });
      } else {
        setIssues(Array.isArray((data as any).data) ? (data as any).data : []);
      }
    } catch (err) {
      console.error(err);
      setError(
        translate("anketa.opsanio.appliance.flyout.error.unexpected", {
          defaultMessage: "Unexpected error",
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [canFetch, appliance, translate]);

  const fetchNotes = useCallback(
    async (issue: GitLabIssue) => {
      if (!canFetch) return;
      setLoadingNotes(true);
      setNotes([]);
      try {
        const { data, error } = await Get({
          path: `v2/${AnketaResourcesEnum.opsanio}/gitlab/project/${issue.project_id}/issue/${issue.iid}/notes`,
          responseType: "json",
          token: keycloak.token,
        });
        if (error) {
          addToast({
            title: translate(`${i18nPrefix}.notes.fetch.error`),
            color: "danger",
            text: error.message,
          });
        } else {
          setNotes(Array.isArray((data as any).data) ? (data as any).data : []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingNotes(false);
      }
    },
    [canFetch],
  );

  const handleSelectIssue = useCallback(
    (issue: GitLabIssue) => {
      setSelected(issue);
      setView("detail");
      fetchNotes(issue);
    },
    [fetchNotes],
  );

  const handleBack = useCallback(() => {
    setView("list");
    setSelected(null);
    setNotes([]);
  }, []);

  const resetFilters = useCallback(() => {
    setSearchText("");
    setStateFilter("all");
    setAssignees([]);
    setDateRange("all");
  }, []);

  useEffect(() => {
    getIssues();
  }, [appliance]);

  const filteredIssues = useMemo(() => {
    const cutoffDays =
      DATE_RANGE_OPTIONS.find((o) => o.key === dateRange)?.days ?? null;
    const cutoff =
      cutoffDays !== null
        ? new Date(Date.now() - cutoffDays * 86_400_000)
        : null;
    const needle = searchText.toLowerCase();

    return issues.filter((issue) => {
      if (needle) {
        const haystack = [
          issue.title,
          issue.author?.name,
          issue.assignee?.name,
          ...(issue.labels ?? []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }

      if (stateFilter !== "all" && issue.state !== stateFilter) return false;

      if (selectedAssignees.length > 0) {
        const relevant = [issue.assignee?.name, issue.author?.name].filter(
          Boolean,
        ) as string[];
        if (!relevant.some((n) => selectedAssignees.includes(n))) return false;
      }

      if (cutoff && new Date(issue.updated_at) < cutoff) return false;

      return true;
    });
  }, [issues, searchText, stateFilter, selectedAssignees, dateRange]);

  const openCount = useMemo(
    () => issues.filter((i) => i.state === "opened").length,
    [issues],
  );
  const closedCount = useMemo(
    () => issues.filter((i) => i.state !== "opened").length,
    [issues],
  );

  const columns: Array<EuiBasicTableColumn<GitLabIssue>> = useMemo(
    () => [
      {
        field: "iid",
        name: "ID",
        width: "68px",
        sortable: true,
        render: (iid: number, item: GitLabIssue) => (
          <EuiLink target="_blank" href={item.web_url}>
            <EuiCode style={{ fontSize: 12 }}>#{iid}</EuiCode>
          </EuiLink>
        ),
      },
      {
        field: "title",
        name: translate("anketa.opsanio.appliance.flyout.gitlab.column.title", {
          defaultMessage: "Title",
        }),
        sortable: true,
        render: (title: string, issue: GitLabIssue) => (
          <div>
            <EuiText size="s" style={{ fontWeight: 500 }}>
              {title}
            </EuiText>
            {issue.labels && issue.labels.length > 0 && (
              <EuiFlexGroup
                gutterSize="xs"
                wrap
                responsive={false}
                style={{ marginTop: 4 }}
              >
                {issue.labels.map((label) => (
                  <EuiFlexItem grow={false} key={label}>
                    <EuiBadge color="hollow" style={{ fontSize: 10 }}>
                      {label}
                    </EuiBadge>
                  </EuiFlexItem>
                ))}
              </EuiFlexGroup>
            )}
          </div>
        ),
      },
      {
        field: "state",
        name: translate(
          "anketa.opsanio.appliance.flyout.gitlab.column.status",
          { defaultMessage: "Status" },
        ),
        width: "130px",
        sortable: true,
        render: (state: string) => (
          <StateBadge state={state} translate={translate} />
        ),
      },
      {
        field: "assignee",
        name: translate(
          "anketa.opsanio.appliance.flyout.gitlab.column.assignee",
          { defaultMessage: "Assignee" },
        ),
        width: "150px",
        render: (assignee: { name: string } | null) => (
          <AssigneeCell assignee={assignee} />
        ),
      },
      {
        field: "updated_at",
        name: translate(
          "anketa.opsanio.appliance.flyout.gitlab.column.updated",
          { defaultMessage: "Updated" },
        ),
        width: "130px",
        sortable: true,
        render: (date: string) => (
          <EuiToolTip content={formatDate(date)}>
            <EuiText size="xs" color="subdued">
              {relativeTime(date, translate)}
            </EuiText>
          </EuiToolTip>
        ),
      },
      {
        width: "48px",
        actions: [
          {
            name: translate("anketa.table.details"),
            description: translate("anketa.table.details"),
            icon: "expand",
            type: "icon",
            onClick: handleSelectIssue,
          },
        ],
      },
    ],
    [handleSelectIssue, formatDate, translate],
  );

  return (
    <EuiPanel
      paddingSize="m"
      hasShadow={false}
      hasBorder={false}
      color="transparent"
    >
      <EuiFlexGroup
        justifyContent="spaceBetween"
        alignItems="center"
        gutterSize="s"
        responsive={false}
      >
        <EuiFlexItem>
          <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiIcon type="logoGitlab" size="l" />
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiTitle size="s">
                <h3 style={{ margin: 0 }}>GitLab Issues</h3>
              </EuiTitle>
            </EuiFlexItem>
            {!loading && issues.length > 0 && (
              <>
                <EuiFlexItem grow={false}>
                  <EuiBadge color="success">
                    {translate(
                      "anketa.opsanio.appliance.flyout.gitlab.badge.open",
                      {
                        values: { count: openCount },
                        defaultMessage: "{{count}} open",
                      },
                    )}
                  </EuiBadge>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiBadge color="hollow">
                    {translate(
                      "anketa.opsanio.appliance.flyout.gitlab.badge.closed",
                      {
                        values: { count: closedCount },
                        defaultMessage: "{{count}} closed",
                      },
                    )}
                  </EuiBadge>
                </EuiFlexItem>
              </>
            )}
          </EuiFlexGroup>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButton
            onClick={getIssues}
            size="s"
            iconType="refresh"
            isLoading={loading}
            disabled={loading}
          >
            {translate("anketa.common.update")}
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiHorizontalRule margin="m" />

      {loading && (
        <EuiFlexGroup
          justifyContent="center"
          alignItems="center"
          style={{ minHeight: 200 }}
        >
          <EuiFlexItem grow={false}>
            <EuiFlexGroup direction="column" alignItems="center" gutterSize="m">
              <EuiLoadingSpinner size="xl" />
              <EuiText size="s" color="subdued">
                {translate(
                  "anketa.opsanio.appliance.flyout.gitlab.loading",
                  { defaultMessage: "Loading issues…" },
                )}
              </EuiText>
            </EuiFlexGroup>
          </EuiFlexItem>
        </EuiFlexGroup>
      )}

      {!loading && error && (
        <EuiCallOut
          title={translate(
            "anketa.opsanio.appliance.flyout.gitlab.error.title",
            { defaultMessage: "Error loading issues" },
          )}
          color="danger"
          iconType="alert"
        >
          <p>{error}</p>
          <EuiButton size="s" color="danger" onClick={getIssues}>
            {translate("anketa.common.retry")}
          </EuiButton>
        </EuiCallOut>
      )}

      {!loading && !error && issues.length === 0 && (
        <EuiEmptyPrompt
          icon={<EuiIcon type="listAdd" size="xl" color="subdued" />}
          title={
            <h3>
              {translate(
                "anketa.opsanio.appliance.flyout.gitlab.empty.title",
                { defaultMessage: "No issues found" },
              )}
            </h3>
          }
          body={
            <EuiText size="s" color="subdued">
              {translate("anketa.opsanio.appliance.flyout.gitlab.empty.body", {
                defaultMessage:
                  "There are currently no GitLab issues for this project.",
              })}
            </EuiText>
          }
        />
      )}

      {!loading && !error && issues.length > 0 && (
        <>
          {view === "list" && (
            <>
              <FilterBar
                issues={issues}
                searchText={searchText}
                onSearchChange={setSearchText}
                stateFilter={stateFilter}
                onStateChange={setStateFilter}
                selectedAssignees={selectedAssignees}
                onAssigneesChange={setAssignees}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                filteredCount={filteredIssues.length}
                totalCount={issues.length}
                onReset={resetFilters}
                translate={translate}
              />

              {filteredIssues.length === 0 ? (
                <EuiEmptyPrompt
                  icon={<EuiIcon type="filter" size="xl" color="subdued" />}
                  title={
                    <h3>
                      {translate(
                        "anketa.opsanio.appliance.flyout.gitlab.noMatch.title",
                        { defaultMessage: "No matches" },
                      )}
                    </h3>
                  }
                  body={
                    <EuiText size="s" color="subdued">
                      {translate(
                        "anketa.opsanio.appliance.flyout.gitlab.noMatch.body",
                        {
                          defaultMessage:
                            "No issues match the current filters.",
                        },
                      )}
                    </EuiText>
                  }
                  actions={
                    <EuiButtonEmpty
                      onClick={resetFilters}
                      iconType="cross"
                      color="danger"
                    >
                      {translate("anketa.common.reset.filters")}
                    </EuiButtonEmpty>
                  }
                />
              ) : (
                <EuiInMemoryTable
                  items={filteredIssues}
                  columns={columns}
                  sorting={{ sort: { field: "updated_at", direction: "desc" } }}
                  pagination={{
                    initialPageSize: 10,
                    pageSizeOptions: [10, 25, 50],
                  }}
                  rowProps={(issue: GitLabIssue) => ({
                    style: {
                      cursor: "pointer",
                      background:
                        selectedIssue?.iid === issue.iid
                          ? "rgba(0,119,204,0.06)"
                          : undefined,
                    },
                    onClick: () => handleSelectIssue(issue),
                  })}
                />
              )}
            </>
          )}

          {view === "detail" && selectedIssue && (
            <IssueDetailView
              issue={selectedIssue}
              notes={notes}
              loadingNotes={loadingNotes}
              formatDate={formatDate}
              onBack={handleBack}
              translate={translate}
            />
          )}
        </>
      )}
    </EuiPanel>
  );
};
